import React from 'react';
import { LAUNCH_DATE, tpchCopy } from '@/data/hardcharger-launch';
import { Link, LocaleImage } from '@/i18n/router';
import { useLocale, useMessages } from '@/i18n/LocaleProvider';
import { blogUi } from '@/i18n/catalogs/editorial/blogUi';
import { blogSectionExcerpts, blogSectionTerms, blogSectionTitles } from '@/i18n/catalogs/editorial/blog-section';
import { blogMetadata } from '@/data/blog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, ArrowRight, User } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';

interface BlogSectionProps {
  // Show only these posts, in this order (default: every post below).
  slugs?: string[];
  anchor?: string;
  title?: string;
  subtitle?: string;
  gaLocation?: string;
}

const BlogSection = ({
  slugs,
  anchor = 'blog',
  title,
  subtitle,
  gaLocation = 'home_blog',
}: BlogSectionProps) => {
  const { locale, formatLocale } = useLocale();
  const tpch = tpchCopy('en'); // the English cards; the Portuguese ones come from blogMetadata
  const c = useMessages(blogUi);
  const englishPosts = [
    {
      slug: 'tpcds-10tb-hardcharger-vs-motherduck',
      title: 'Bigger Ducks, Faster: Hardcharger on GizmoData Cloud vs. MotherDuck at 1 TB and 10 TB',
      excerpt: '99 queries derived from TPC-DS on a 192-vCPU Graviton5 machine: a 0.46 s geometric mean at 1 TB and 3.39 s at 10 TB, against MotherDuck Giga\'s published 0.69 s and 5.6 s, and ahead of every system MotherDuck cited. Per-query times, caveats and cost.',
      date: '2026-11-03',
      readTime: '9 min read',
      category: 'Performance',
      author: 'Philip Moore',
      tags: ['Benchmark', 'TPC-DS', 'Hardcharger', 'MotherDuck', 'DuckDB 2.0'],
      image: '/blog/tpcds-10tb-hardcharger-vs-motherduck/hero-v2.jpg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'what-the-heck-is-hardcharger',
      title: "What the Heck is Hardcharger?",
      excerpt: "Shawn Gordon follows up his GizmoSQL article with its successor: an open-source DuckDB 2.0 server on Chargewire (SQL over plain HTTP, Arrow results), every former Enterprise feature included, and a local DuckDB file shared on GizmoData Cloud that Claude queries over MCP. Originally on HackerNoon.",
      date: '2026-10-30',
      readTime: '6 min read',
      category: 'Guest Article',
      author: 'Shawn Gordon',
      tags: ['Guest Article', 'Hardcharger', 'Chargewire', 'GizmoData Cloud', 'MCP'],
      image: '/blog/what-the-heck-is-hardcharger/hero-800w.webp',
      imageWidth: 1168,
      imageHeight: 784
    },
    {
      slug: 'introducing-hardcharger',
      title: "Introducing Hardcharger™: The DuckDB 2.0 Server, Built on the Open Chargewire Protocol",
      excerpt: `Our new open-source DuckDB 2.0 server speaks Chargewire, an open protocol for SQL over HTTP with Arrow results. 22 queries derived from TPC-H over 1 TB in ${tpch.hcSeconds} s on one Graviton5 machine, about ${tpch.hcCents} cents of compute, and every feature included: RBAC, SSO, mTLS, audit and a web UI. No license keys.`,
      date: LAUNCH_DATE,
      readTime: '12 min read',
      category: 'Release',
      author: 'Philip Moore',
      tags: ['Hardcharger', 'Chargewire', 'DuckDB 2.0', 'Open Source', 'Performance'],
      image: '/blog/introducing-hardcharger/hero-v2-800w.webp',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'adbc-replicate-snowflake-oracle-ai-agents',
      title: 'Replicate Once, Query All Day: adbc-replicate Feeds AI Agents from Snowflake and Oracle',
      excerpt: 'Our new open-source adbc-replicate streams Snowflake, Oracle, PostgreSQL or DuckDB schemas into GizmoSQL as Arrow over ADBC, masking inside the source and copying only what changed. Two customers, on Snowflake and Oracle, run it hourly, and their AI agents query the copy over MCP all day.',
      date: '2026-10-16',
      readTime: '11 min read',
      category: 'Release',
      author: 'Philip Moore',
      tags: ['adbc-replicate', 'Snowflake', 'Oracle', 'MCP', 'ADBC'],
      image: '/adbc-replicate-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'tpch-1tb-horse-race-aws-gcp-azure',
      // Card title, worded for the TPC's fair-use rules (the post keeps its original title).
      title: 'Three Clouds, One API: A 1 TB Horse Race Derived from TPC-H on GizmoData Cloud (AWS vs. Google Cloud vs. Azure)',
      excerpt: 'Six 64-vCPU GizmoSQL clusters on AWS, Google Cloud and Azure raced a 1 TB workload derived from TPC-H on local NVMe and DuckLake. Graviton5 ran the fastest lap (79 s, $0.30), Google Cloud took four of six golds, and DuckDB 2.0 won every lane. 38 minutes, about $37, one API.',
      date: '2026-10-08',
      readTime: '9 min read',
      category: 'Performance',
      author: 'Philip Moore',
      tags: ['Benchmark', 'GizmoData Cloud', 'DuckLake', 'DuckDB 2.0'],
      image: '/blog/tpch-1tb-horse-race/hero-800w.webp',
      imageWidth: 1600,
      imageHeight: 901
    },
    {
      slug: 'introducing-gizmodata-cloud',
      title: 'Introducing GizmoData Cloud: The Best Way to Run DuckDB in the Cloud',
      excerpt: 'GizmoData Cloud is generally available: fully managed GizmoSQL clusters, DuckLake catalogs and federated queries on AWS, Google Cloud and Azure. Sign up free. Start free with $25 of launch credit, applied automatically, no credit card.',
      date: '2026-09-28',
      readTime: '6 min read',
      category: 'Release',
      author: 'Philip Moore',
      tags: ['GizmoData Cloud', 'GizmoSQL', 'DuckDB', 'DuckLake', 'Managed Service'],
      image: '/gizmodata-cloud-launch-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'gizmosql-session-limits-idle-timeouts',
      title: 'The Connection That Never Left: Keeping GizmoSQL Lean With Session Limits and Idle Timeouts',
      excerpt: 'Nobody ran a query since Friday, yet Monday morning the server is at 80% memory: the queries left, the connections didn\'t. Guest author Emmanuel Sibanda on --max-sessions and --session-idle-timeout, and why only real user SQL resets the idle clock.',
      date: '2026-09-15',
      readTime: '5 min read',
      category: 'Guest Article',
      author: 'Emmanuel Sibanda',
      tags: ['Guest Article', 'GizmoSQL', 'Operations', 'Sessions', 'DuckDB'],
      image: '/gizmosql-idle-sessions-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'gizmosql-ducklake-elastic-compute-shared-state',
      title: 'GizmoSQL + DuckLake: Elastic Compute Over Shared State',
      excerpt: 'Scaling nodes that share mutable data is the hard part of analytics. With DuckLake holding the transactional catalog and Parquet in object storage, a GizmoSQL cluster grows from one node to five and back with nothing to copy, sync, or reconcile.',
      date: '2026-09-11',
      readTime: '5 min read',
      category: 'Architecture',
      author: 'Chris Wones',
      tags: ['DuckLake', 'GizmoSQL', 'Autoscaling', 'Lakehouse', 'DuckDB'],
      image: '/gizmosql-ducklake-elastic-compute-hero.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'seagull-scalable-web-gis-gizmosql',
      title: 'Seagull: How to Use GizmoSQL to Build Scalable Web GIS Applications',
      excerpt: 'Aalborg University\'s Seagull platform serves 18.7 billion AIS records as live map layers, every tile computed on request from DuckDB. GizmoSQL made single-writer DuckDB a shared database for Airflow, MapLibre Martin, and the web app.',
      date: '2026-09-08',
      readTime: '6 min read',
      category: 'Guest Article',
      author: 'Kasper F. Pedersen',
      tags: ['Guest Article', 'GIS', 'Maritime', 'DuckDB', 'GizmoSQL'],
      image: '/seagull-gizmosql-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'tpch-1tb-graviton5-80-seconds',
      // Card title, worded for the TPC's fair-use rules (the post keeps its original title).
      title: '1 TB in 80 Seconds, Derived from TPC-H: GizmoSQL on AWS Graviton5 (vs. Graviton4 and Azure Cobalt 100)',
      excerpt: 'A 1 TB workload derived from TPC-H (22 queries) on a brand-new Graviton5 r9gd.24xlarge: 80.5 seconds, 10% faster than Graviton4 and 25% faster than Azure Cobalt 100 in same-day, same-DuckDB runs, still $0.17 of compute. Methodology and every per-query timing.',
      date: '2026-09-04',
      readTime: '10 min read',
      category: 'Performance',
      author: 'Philip Moore',
      tags: ['Benchmark', 'Graviton5', 'Graviton4', 'GizmoSQL', 'DuckDB'],
      image: '/tpch-1tb-graviton5-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'introducing-gizmosql-mcp-server',
      title: 'Introducing the GizmoSQL MCP Server: Talk to Your DuckDB Data from Claude',
      excerpt: 'An MCP server and Claude Desktop extension for GizmoSQL — Claude Desktop, Claude Code, and any MCP client can explore your schema and run SQL, read-only by default with server-enforced row caps and timeouts.',
      date: '2026-09-04',
      readTime: '7 min read',
      category: 'Release',
      author: 'Philip Moore',
      tags: ['MCP', 'Claude', 'AI Agents', 'GizmoSQL', 'DuckDB'],
      image: '/gizmosql-mcp-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'gizmodata-adbc-driver-suite',
      title: 'Meet the GizmoData ADBC Driver Suite: Move Data Between Oracle, Db2, and GizmoSQL',
      excerpt: "Pure-Go, pip-installable ADBC drivers for Oracle (no Instant Client), IBM Db2 (no CLI/JCC), and DuckDB's Quack — alongside our flagship GizmoSQL driver. Arrow-native, so moving a table between them is a few lines of Python or one SQL statement, in both directions.",
      date: '2026-08-27',
      readTime: '9 min read',
      category: 'Release',
      author: 'Philip Moore',
      tags: ['ADBC', 'Oracle', 'Db2', 'GizmoSQL', 'Apache Arrow'],
      image: '/adbc-drivers-launch-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'gizmosql-bulk-ingest-arrow-scan-rewrite',
      title: 'The Arrow Scan Rewrite: How GizmoSQL v1.37.0 Made Bulk Ingest 7x Faster',
      excerpt: "GizmoSQL v1.37.0 stopped hand-converting every Arrow cell and started streaming batches through DuckDB's own arrow_scan instead — 7.2x faster bulk loads, exact decimals, and geometry columns that finally land as GEOMETRY.",
      date: '2026-08-24',
      readTime: '10 min read',
      category: 'Guest Article',
      author: 'Chris Harrison',
      tags: ['GizmoSQL', 'Bulk Ingestion', 'Arrow Flight SQL', 'DuckDB', 'Performance'],
      image: '/gizmosql-bulk-ingest-arrow-scan-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'gizmosql-adbc-v2-go-driver',
      title: 'GizmoSQL ADBC Driver 2.0: A Go-Powered Core That Brings GizmoSQL to Every ADBC Language',
      excerpt: 'The GizmoSQL ADBC driver has been rebuilt in Go with a thin, drop-in-compatible Python wrapper — bringing OAuth/SSO, automatic DDL/DML detection, and more to every ADBC language.',
      date: '2026-07-29',
      readTime: '8 min read',
      category: 'Release',
      author: 'Philip Moore',
      tags: ['ADBC', 'Go', 'Python', 'Arrow Flight SQL', 'GizmoSQL'],
      image: '/gizmosql-adbc-v2-blog.svg',
      imageWidth: 1600,
      imageHeight: 900
    },
    {
      slug: 'welcoming-quack-duckdb-client-server',
      title: "Welcoming Quack: DuckDB's New Client-Server Protocol — and Where GizmoSQL Goes From Here",
      excerpt: "DuckDB Labs announced Quack, a native client-server protocol for DuckDB. A thank-you to the team — and a look at how GizmoSQL complements the growing client-server DuckDB ecosystem.",
      date: '2026-05-13',
      readTime: '6 min read',
      category: 'Commentary',
      author: 'Philip Moore',
      tags: ['GizmoSQL', 'DuckDB', 'Quack', 'Arrow Flight SQL'],
      image: '/welcoming-quack-blog.webp',
      imageWidth: 1600,
      imageHeight: 800
    },
    {
      slug: 'freeing-individuals-from-the-matrix',
      title: 'Freeing the Individuals from the Matrix: How GizmoSQL Makes Your Database AI-Agent Ready',
      excerpt: 'A Matrix-themed deep dive into building an AI-agent-ready architecture with GizmoSQL, DuckDB, and Claude—simulating one million people and monitoring anomalies with an autonomous AI agent.',
      date: '2026-03-05',
      readTime: '15 min read',
      category: 'Guest Article',
      author: 'Chris Harrison',
      tags: ['GizmoSQL', 'AI Agents', 'DuckDB', 'Claude', 'Apache Arrow'],
      image: '/gizmosql-matrix-blog.webp',
      imageWidth: 1536,
      imageHeight: 1024
    },
    {
      slug: 'adbc-flightsql-bulk-ingestion',
      title: 'ADBC Flight SQL 1.10.0: Bulk Ingestion for GizmoSQL',
      excerpt: 'The new ADBC Flight SQL Python driver 1.10.0 brings bulk ingestion to Arrow Flight SQL servers like GizmoSQL. Load millions of rows directly from Python or DuckDB with automatic schema inference.',
      date: '2026-01-10',
      readTime: '6 min read',
      category: 'Release',
      author: 'GizmoData Team',
      tags: ['ADBC', 'Arrow Flight SQL', 'GizmoSQL', 'Python', 'DuckDB'],
      image: '/adbc-bulk-ingestion-blog.webp',
      imageWidth: 1536,
      imageHeight: 1024
    },
    {
      slug: 'what-the-heck-is-gizmosql',
      title: 'What the Heck is GizmoSQL?',
      excerpt: 'A deep dive into GizmoSQL—a small server that runs DuckDB with the Arrow Flight SQL protocol wrapped around it so you can run DuckDB remotely.',
      date: '2025-12-30',
      readTime: '6 min read',
      category: 'Guest Article',
      author: 'Shawn Gordon',
      tags: ['GizmoSQL', 'DuckDB', 'Apache Arrow', 'Arrow Flight SQL'],
      image: '/what-the-heck-is-gizmosql-blog.webp',
      imageWidth: 1280,
      imageHeight: 1211
    },
    {
      slug: 'introducing-gizmosql-operator',
      title: 'Introducing GizmoSQL Operator',
      excerpt: 'Announcing the GizmoSQL Kubernetes Operator—a tool to reduce operational costs of running large-scale SQL queries.',
      date: '2025-12-19',
      readTime: '5 min read',
      category: 'Guest Article',
      author: 'Nima Khoshini',
      tags: ['GizmoSQL', 'Kubernetes', 'Operator', 'DevOps'],
      image: '/gizmosql-operator-blog.webp',
      imageWidth: 1024,
      imageHeight: 1024
    },
    {
      slug: 'gizmoedge-one-trillion-row-challenge',
      title: 'GizmoEdge Takes on the 1 Trillion Row Challenge',
      excerpt: 'What happens when you give a distributed SQL engine a trillion-row dataset? GizmoEdge crushed the Coiled challenge with a 1,000-worker cluster on Azure.',
      date: '2025-10-22',
      readTime: '8 min read',
      category: 'Performance',
      author: 'GizmoData Team',
      tags: ['GizmoEdge', 'DuckDB', 'Big Data', 'Performance', 'Distributed Computing'],
      image: '/gizmoedge-1trc-blog.webp',
      imageWidth: 1536,
      imageHeight: 1024
    },
    {
      slug: 'gizmosql-one-trillion-row-challenge',
      title: 'GizmoSQL One-Trillion Row Challenge Results',
      excerpt: 'GizmoSQL completed the 1 trillion row challenge! Learn how we processed massive datasets using DuckDB and Apache Arrow Flight SQL.',
      date: '2025-07-15',
      readTime: '5 min read',
      category: 'Performance',
      author: 'GizmoData Team',
      tags: ['GizmoSQL', 'DuckDB', 'Big Data', 'Performance'],
      image: '/gizmosql-1trc-blog.webp',
      imageWidth: 1536,
      imageHeight: 1024
    }
  ];

  // Portuguese: the same curated cards, with the Portuguese title from the blog metadata and the
  // card's own excerpt translated in the blog-section catalog.
  const ptPosts = blogMetadata['pt-BR'] as Record<string, { title: string } | undefined>;
  const term = (value: string) => blogSectionTerms[value] ?? value;
  const allPosts = locale === 'pt-BR'
    ? englishPosts.map((post) => ({
        ...post,
        title: blogSectionTitles[post.slug] ?? ptPosts[post.slug]?.title ?? post.title,
        excerpt: blogSectionExcerpts[post.slug] ?? post.excerpt,
        category: term(post.category),
        readTime: post.readTime.replace(/ min read$/, ' min de leitura'),
        tags: post.tags.map(term),
      }))
    : englishPosts;

  const blogPosts = slugs
    ? slugs.map((slug) => allPosts.find((p) => p.slug === slug)).filter((p): p is (typeof allPosts)[number] => !!p)
    : allPosts;

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gizmo-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <SectionHeading withId anchor={anchor} className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {title ?? c.latest}
          </SectionHeading>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {subtitle ?? c.latestIntro}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {blogPosts.map((post) => (
            <div key={post.slug} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl dark:hover:shadow-white/20 transition-shadow duration-300 overflow-hidden">
              <Link
                to={`/blog/${post.slug}`}
                className="block aspect-video bg-gradient-to-br from-gizmo-500 to-blue-600"
                aria-label={post.title}
                data-ga-cta="read_blog_post"
                data-ga-location={gaLocation}
                data-ga-text={post.title}
              >
                <LocaleImage
                  src={post.image}
                  alt={post.title}
                  width={post.imageWidth}
                  height={post.imageHeight}
                  className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                  loading="lazy"
                />
              </Link>
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <Badge variant="secondary" className="bg-gizmo-100 text-gizmo-800 dark:bg-gizmo-800 dark:text-gizmo-200">
                    {post.category}
                  </Badge>
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="h-4 w-4 mr-1" />
                    {new Date(post.date).toLocaleDateString(formatLocale, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC'
                    })}
                  </div>
                </div>

                <Link to={`/blog/${post.slug}`} data-ga-cta="read_blog_post" data-ga-location={gaLocation} data-ga-text={post.title}>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 hover:text-gizmo-600 dark:hover:text-gizmo-400 transition-colors">
                    {post.title}
                  </h3>
                </Link>

                <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <User className="h-4 w-4 mr-1" />
                      {post.author}
                    </div>
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <Clock className="h-4 w-4 mr-1" />
                      {post.readTime}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <Link
                  to={`/blog/${post.slug}`}
                  aria-label={`${locale === 'pt-BR' ? 'Leia mais' : 'Read more'}: ${post.title}`}
                  data-ga-cta="read_blog_post"
                  data-ga-location={gaLocation}
                  data-ga-text={post.title}
                >
                  <Button variant="outline" className="w-full group">
                    {c.readMore}
                    {/* sr-only title makes the link text descriptive for the SEO link-text
                        audit (which reads text content, not aria-label) without changing the UI. */}
                    <span className="sr-only">: {post.title}</span>
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link to="/blog" aria-label={c.allPostsLabel} data-ga-cta="read_blog" data-ga-location={gaLocation}>
            <Button size="lg" className="bg-gizmo-600 hover:bg-gizmo-700 text-white">
              {c.allPosts}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;